import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { getEquipmentKBDefinition, updateEquipment } from "@/lib/api/building";
import {
  equipmentCandidateGroups,
  type RemapCandidateGroup,
} from "@/lib/kb-remap-groups";
import { cardTonePill } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { dataPointLabel } from "@/lib/data-point-labels";
import {
  activate_observed_catalogue_ids,
  aktivoimatta_catalogue_ids,
  aktivoimatta_lahdesignaali_label,
  catalogue_ids_from_groups,
  flatten_activation_rows,
  sorted_declared_ids,
  toggle_declared_id,
} from "./hallitse-signaaleja";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
  equipmentId: string;
  equipmentName: string;
  declaredDataPoints: string[];
  /** Observed canonical ids (list / sim / CSV history) — same set as Signaalit rows. */
  observedDataPoints?: string[];
  /** Canonical id → taught Lähdesignaali for aktivoimatta rows. */
  sourceSignals?: Record<string, string>;
  labels?: Record<string, string>;
  onSaved: () => void;
};

/**
 * Activate / deactivate KB signaalit via checkboxes. Writes only
 * `declared_data_points` via PATCH — never `PUT .../signal-map`.
 * Observed+kytketty-but-undeclared catalogue ids float to the top (aktivoimatta).
 * Observed without Lähdesignaali is a kytkennät gap, not an aktivointi suggestion.
 */
export function HallitseSignaalejaDialog({
  open,
  onOpenChange,
  buildingId,
  equipmentId,
  equipmentName,
  declaredDataPoints,
  observedDataPoints = [],
  sourceSignals = {},
  labels,
  onSaved,
}: Props) {
  const [candidateGroups, setCandidateGroups] = useState<RemapCandidateGroup[]>(
    [],
  );
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChecked(new Set(declaredDataPoints));
    setError(null);
    setLoading(true);
    getEquipmentKBDefinition(buildingId, equipmentId)
      .then((def) => setCandidateGroups(equipmentCandidateGroups(def)))
      .catch(() => setCandidateGroups([]))
      .finally(() => setLoading(false));
  }, [open, buildingId, equipmentId, declaredDataPoints]);

  const catalogueIds = useMemo(
    () => catalogue_ids_from_groups(candidateGroups),
    [candidateGroups],
  );

  const observedSet = useMemo(
    () => new Set(observedDataPoints),
    [observedDataPoints],
  );

  const pendingIds = useMemo(
    () =>
      aktivoimatta_catalogue_ids(
        catalogueIds,
        observedSet,
        checked,
        sourceSignals,
      ),
    [catalogueIds, observedSet, checked, sourceSignals],
  );

  const pendingIdSet = useMemo(() => new Set(pendingIds), [pendingIds]);

  const flatRows = useMemo(
    () =>
      flatten_activation_rows(
        candidateGroups,
        observedSet,
        checked,
        sourceSignals,
      ),
    [candidateGroups, observedSet, checked, sourceSignals],
  );

  function setRow(id: string, on: boolean) {
    setChecked((prev) => toggle_declared_id(prev, id, on));
  }

  function activateObserved() {
    setChecked((prev) =>
      activate_observed_catalogue_ids(
        prev,
        catalogueIds,
        observedSet,
        sourceSignals,
      ),
    );
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await updateEquipment(buildingId, equipmentId, {
        declared_data_points: sorted_declared_ids(checked, catalogueIds),
      });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[min(100%,56rem)] max-w-5xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Signaalien aktivointi · {equipmentName}</DialogTitle>
          <DialogDescription>
            Valittu = käytössä. Aktivoimatta-ehdotukset tarvitsevat lähdesignaalin
            (kytkentä ensin) — sama joukko kuin listan aktivoimatta-merkki.
            Tallennus vasta Tallenna-painikkeella.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto py-1 pr-3 [scrollbar-gutter:stable]">
          {loading ? (
            <p className="text-[11px] text-muted-foreground px-1">Ladataan…</p>
          ) : candidateGroups.length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-1">
              KB-luetteloa ei saatu ladattua.
            </p>
          ) : (
            <div className="space-y-2">
              {pendingIds.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                  <span className="text-[11px] text-muted-foreground">
                    {pendingIds.length} havaittua signaalia aktivoimatta
                  </span>
                  <button
                    type="button"
                    onClick={activateObserved}
                    className="rounded-md border border-border bg-surface px-2.5 py-1 text-[10px] font-medium hover:border-primary/30"
                  >
                    Aktivoi havaitut
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2.5 px-1 py-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                <span className="w-4 shrink-0" />
                <span className="flex-1">Signaali</span>
                <span className="w-36 shrink-0">Lähdesignaali</span>
                <span className="w-24 shrink-0" />
                <span className="w-40 shrink-0 text-right">Komponentti</span>
              </div>
              <div className="divide-y divide-border/20">
                {flatRows.map((row) => {
                  const label =
                    row.name_fi || dataPointLabel(row.id, labels);
                  const isOn = checked.has(row.id);
                  const isPending = pendingIdSet.has(row.id);
                  const lahde = aktivoimatta_lahdesignaali_label(
                    sourceSignals,
                    row.id,
                  );
                  return (
                    <div
                      key={row.id}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 px-1 py-2 text-[11px] hover:bg-muted/30",
                        isPending && "bg-warning/5",
                      )}
                      onClick={() => setRow(row.id, !isOn)}
                    >
                      <Checkbox
                        checked={isOn}
                        onCheckedChange={(value) =>
                          setRow(row.id, value === true)
                        }
                        onClick={(event) => event.stopPropagation()}
                        aria-label={label}
                      />
                      <span
                        className="min-w-0 flex-1 text-foreground/90"
                        title={label}
                      >
                        {label}
                      </span>
                      <span
                        className={cn(
                          "w-36 shrink-0 truncate font-mono text-[10px]",
                          lahde === "ei lähdesignaalia"
                            ? "text-muted-foreground/70"
                            : "text-muted-foreground",
                        )}
                        title={lahde}
                      >
                        {lahde}
                      </span>
                      {isPending ? (
                        <span
                          className={cn(
                            "w-24 shrink-0 rounded-full border px-2 py-0.5 text-center text-[10px]",
                            cardTonePill.warn,
                          )}
                        >
                          aktivoimatta
                        </span>
                      ) : (
                        <span className="w-24 shrink-0" />
                      )}
                      {row.groupLabel ? (
                        <span
                          className="w-40 shrink-0 truncate text-right text-muted-foreground"
                          title={row.groupLabel}
                        >
                          {row.groupLabel}
                        </span>
                      ) : (
                        <span className="w-40 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <p className="mt-2 text-[11px] text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="rounded-md border border-border bg-surface px-3 py-2 text-xs font-medium hover:border-primary/30"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || loading || candidateGroups.length === 0}
            className="rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Tallennetaan…" : "Tallenna"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
